// src/hooks/useBreadcrumbs.ts
// Dynamic breadcrumb resolver for the navbar integration
// Generates breadcrumb crumbs from the current URL path

import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { allPosts } from '../pages/Insights/Articles/ArticleData';
import { tutorialCategories, courses } from '../pages/Insights/Courses/CourseData';

export interface CrumbItem {
    path: string;
    label: string;
}

export interface BreadcrumbResult {
    crumbs: CrumbItem[];
    depth: number;
}

/**
 * Static route-to-label mapping for known paths.
 * Covers all routes defined in App.tsx.
 */
const ROUTE_LABELS: Record<string, string> = {
    '/': 'Home',
    '/tools': 'Tools',
    '/dev-hub': 'Developer Hub',
    '/insights': 'Insights',
    '/about': 'About Us',
    '/contact': 'Contact',
    '/privacy': 'Privacy Policy',
    '/terms': 'Terms of Service',
    '/features': 'Features',
    '/faq': 'FAQ',

    // Tool sub-pages
    '/tools/compare-text': 'Text Compare',
    '/tools/compress-pdf-images': 'File Compressor',
    '/tools/merge-split-pdf': 'Merge & Split PDF',
    '/tools/qr-code-generator': 'QR Generator',
    '/tools/barcode-scanner': 'Barcode Scanner',
    '/tools/scan-image-to-text': 'OCR Extractor',
    '/tools/remove-background': 'Background Remover',
    '/tools/json-validator-formatter': 'JSON Validator',

    // DevHub sub-pages
    '/dev-hub/mock-api': 'Mock API',
    '/dev-hub/webhook-tester': 'Webhook Tester',
    '/dev-hub/email-sandbox': 'Email Sandbox',
    '/dev-hub/payment-simulator': 'Payment Simulator',

    // Insights sub-pages
    '/insights/articles': 'Articles',
    '/insights/courses': 'Courses',
};

/**
 * Maps a parent path to its known parent chain.
 */
const PARENT_CHAINS: Record<string, string[]> = {
    // Tools
    '/tools/compare-text': ['/', '/tools'],
    '/tools/compress-pdf-images': ['/', '/tools'],
    '/tools/merge-split-pdf': ['/', '/tools'],
    '/tools/qr-code-generator': ['/', '/tools'],
    '/tools/barcode-scanner': ['/', '/tools'],
    '/tools/scan-image-to-text': ['/', '/tools'],
    '/tools/remove-background': ['/', '/tools'],
    '/tools/json-validator-formatter': ['/', '/tools'],

    // DevHub
    '/dev-hub/mock-api': ['/', '/dev-hub'],
    '/dev-hub/webhook-tester': ['/', '/dev-hub'],
    '/dev-hub/email-sandbox': ['/', '/dev-hub'],
    '/dev-hub/payment-simulator': ['/', '/dev-hub'],

    // Insights
    '/insights/articles': ['/', '/insights'],
    '/insights/courses': ['/', '/insights'],
};

/**
 * Fallback: convert a URL slug to a human-readable title.
 * e.g., "my-article-about-react" → "My Article About React"
 */
function slugToTitle(slug: string): string {
    return slug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/**
 * Look up the real article title by slug from allPosts data.
 */
function getArticleTitle(slug: string): string {
    const post = allPosts.find(p => p.slug === slug);
    return post?.title || slugToTitle(slug);
}

/**
 * Look up the proper course/language name from tutorialCategories.
 * e.g., "html" → "HTML", "python" → "Python", "javascript" → "JavaScript"
 */
function getCourseLanguageName(languageSlug: string): string {
    const category = tutorialCategories.find(c => c.id === languageSlug);
    return category?.name || slugToTitle(languageSlug);
}

/**
 * Look up the real lesson title by slug from courses data.
 */
function getLessonTitle(languageSlug: string, lessonSlug: string): string {
    const course = courses.find(c => c.language === languageSlug);
    if (course) {
        for (const chapter of course.chapters) {
            const lesson = chapter.lessons.find(l => l.slug === lessonSlug);
            if (lesson) return lesson.title;
        }
    }
    return slugToTitle(lessonSlug);
}

/**
 * Custom hook that resolves the current URL into a breadcrumb trail.
 * 
 * Rules:
 * - Homepage (/) returns depth 0 — show normal nav
 * - Top-level pages (/tools, /insights, etc.) return depth 1 — show normal nav with highlight
 * - Sub-pages (/tools/json-validator, /dev-hub/mock-api) return depth 2+ — show breadcrumbs
 * - Dynamic article/course pages return depth 3+ with real titles from data
 */
export function useBreadcrumbs(): BreadcrumbResult {
    const location = useLocation();

    return useMemo(() => {
        const pathname = location.pathname;

        // Homepage — no breadcrumbs needed
        if (pathname === '/') {
            return { crumbs: [], depth: 0 };
        }

        // Check for exact match in route labels
        if (ROUTE_LABELS[pathname]) {
            const parentChain = PARENT_CHAINS[pathname];

            if (parentChain) {
                // Has a known parent chain — build full breadcrumb
                const crumbs: CrumbItem[] = parentChain.map(p => ({
                    path: p,
                    label: ROUTE_LABELS[p] || slugToTitle(p.split('/').pop() || ''),
                }));
                crumbs.push({ path: pathname, label: ROUTE_LABELS[pathname] });
                return { crumbs, depth: crumbs.length - 1 };
            }

            // Single-level page (e.g., /about, /contact, /faq) — depth 1
            return {
                crumbs: [
                    { path: '/', label: 'Home' },
                    { path: pathname, label: ROUTE_LABELS[pathname] },
                ],
                depth: 1,
            };
        }

        // Dynamic routes — build crumbs by analyzing the path
        const segments = pathname.split('/').filter(Boolean);

        // Handle /insights/articles/:slug (article detail)
        if (segments[0] === 'insights' && segments[1] === 'articles' && segments[2]) {
            return {
                crumbs: [
                    { path: '/', label: 'Home' },
                    { path: '/insights', label: 'Insights' },
                    { path: '/insights/articles', label: 'Articles' },
                    { path: pathname, label: getArticleTitle(segments[2]) },
                ],
                depth: 3,
            };
        }

        // Handle /insights/courses/:language (course overview)
        if (segments[0] === 'insights' && segments[1] === 'courses' && segments[2] && !segments[3]) {
            return {
                crumbs: [
                    { path: '/', label: 'Home' },
                    { path: '/insights', label: 'Insights' },
                    { path: '/insights/courses', label: 'Courses' },
                    { path: pathname, label: getCourseLanguageName(segments[2]) },
                ],
                depth: 3,
            };
        }

        // Handle /insights/courses/:language/:slug (course lesson)
        if (segments[0] === 'insights' && segments[1] === 'courses' && segments[2] && segments[3]) {
            return {
                crumbs: [
                    { path: '/', label: 'Home' },
                    { path: '/insights', label: 'Insights' },
                    { path: '/insights/courses', label: 'Courses' },
                    { path: `/insights/courses/${segments[2]}`, label: getCourseLanguageName(segments[2]) },
                    { path: pathname, label: getLessonTitle(segments[2], segments[3]) },
                ],
                depth: 4,
            };
        }

        // Fallback: build from URL segments
        const crumbs: CrumbItem[] = [{ path: '/', label: 'Home' }];
        let currentPath = '';
        for (const segment of segments) {
            currentPath += `/${segment}`;
            crumbs.push({
                path: currentPath,
                label: ROUTE_LABELS[currentPath] || slugToTitle(segment),
            });
        }

        return { crumbs, depth: crumbs.length - 1 };
    }, [location.pathname]);
}

export default useBreadcrumbs;
